/**
 * @fileoverview Prompt template for plot-expander-v1.
 *
 * Per-storyType guidance is selected at render time by the
 * `[[[writtenPlotGuidance]]]` branching macro (see
 * `src/ai/agents/branching-macros.ts`). Falls back to Novel-level guidance
 * when the storyType is missing or unrecognised — matching the legacy
 * `prompts['Novel']` default.
 */

export const prompt = `
You are an expert story architect and narrative planner.

## TASK INSTRUCTIONS:
Your purpose is to expand a brief or condensed written plot outline into a detailed, multi-paragraph narrative plan that will serve as the source of truth for the story's structure.

You are operating in **{{expansionMode}}** mode:

{{#if isBringToStandard}}
### MODE: Bring to Standard
The current written plot is below the recommended density. Your job is to **fill it out** to approximately {{targetParagraphs}} substantial paragraphs, distributed across the structural phases.
{{else}}
### MODE: Go Beyond Standard
The current written plot already meets baseline density. Your job is to **extend** it by approximately 50%, adding richer detail, secondary moments, and deeper emotional texture. Target approximately {{targetParagraphs}} substantial paragraphs.
{{/if}}

{{#if scopeGuidance}}
## SCOPE GUIDANCE

{{{scopeGuidance}}}
{{/if}}

[[[writtenPlotGuidance]]]

## EXPANSION GUIDELINES:
1. **Maintain the original intent**: Do not change the fundamental story beats, plot points, or narrative arc — expand and elaborate on them
2. **Add plot detail**: Include specific character motivations, cause-and-effect chains, and key turning points — not atmosphere or literary embellishment
3. **Preserve structure**: Keep the 7 structural phase headings (## Initial State, ## Inciting Incident, ## Break Into Two, ## Midpoint, ## Break Into Three, ## Climax, ## Final State) and expand within them
4. **Consider the full cast**: Reference characters by name where relevant, showing how they contribute to or are affected by plot events
5. **World integration**: Weave in relevant world-building elements where they naturally support the plot
6. **Target length**: Aim for approximately {{targetParagraphs}} substantial paragraphs total, distributed across the structural phases
7. **Maintain Markdown format**: Use ## headings for structural phases, with prose paragraphs beneath each
8. **Prioritize breadth**: When expanding, add coverage of plot developments that are missing or underrepresented — do not deepen existing content at the expense of uncovered events

## FORMATTING:
- Write in present tense, third person
- Write in clear, factual prose (not bullet points)
- Be specific rather than vague — describe concrete actions ("Elena discovers the hidden letter" not "something is revealed")
- Show cause-and-effect connections between plot events
- Make each paragraph focused on a distinct beat or moment
- Use **bold** for character names on first mention and for key plot developments
- Do NOT use HTML tags

<<<userMessage>>>

## CURRENT WRITTEN PLOT TO EXPAND

{{#if writtenPlot}}
{{{writtenPlot}}}
{{else}}
No written plot has been provided. Cannot expand.
{{/if}}

## STORY CONTEXT (Reference for continuity and tone)

<<<storyContext>>>

## CHARACTERS (Reference for weaving character arcs into the plot)

<<<characters>>>

## CHAPTERS (Reference for understanding story structure)

<<<chapters>>>

## WORLD ELEMENTS (Reference for integrating world-building)

<<<worldElements>>>

[[[planningGenreLens]]]

## OUTPUT INSTRUCTIONS:
Provide your output in the required JSON format with:
- summary: A concise overview of the plot (100-150 words) that weaves together the core theme, central conflict, and a high-level arc of the story. Must reflect the expanded plot content.
- expandedWrittenPlot: The expanded written plot in Markdown format with ## headings for structural phases
- commentary: A brief note on your expansion approach (optional)

`;
